import { useState } from "react";
import {
  Card,
  CardContent,
  Label,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@geely/ui";
import { CalendarDays, X, Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@geely/ui";

export const ALL_DATES_VALUE = 'all';

interface DateFilterProps {
  selectedDate: string;
  availableDates: string[];
  onDateChange: (date: string) => void;
}

function formatDateLabel(date: string): string {
  return new Date(date + 'T00:00:00').toLocaleDateString('pt-BR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatShortDate(date: string): string {
  return new Date(date + 'T00:00:00').toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatCompactDate(date: string): string {
  const dateObj = new Date(date + 'T00:00:00');
  const weekday = dateObj.toLocaleDateString('pt-BR', { weekday: 'short' });
  const shortDate = formatShortDate(date);
  return `${shortDate} (${weekday})`;
}

function formatSearchableDate(date: string): string {
  const dateObj = new Date(date + 'T00:00:00');
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = dateObj.toLocaleDateString('pt-BR', { month: 'long' });
  const monthShort = dateObj.toLocaleDateString('pt-BR', { month: 'short' });
  const year = dateObj.getFullYear();
  const weekday = dateObj.toLocaleDateString('pt-BR', { weekday: 'long' });

  // Retorna string pesquisável com múltiplos formatos
  return `${day} ${month} ${monthShort} ${year} ${weekday}`.toLowerCase();
}

export function DateFilter({ selectedDate, availableDates, onDateChange }: DateFilterProps) {
  const [open, setOpen] = useState(false);
  const isAllSelected = selectedDate === ALL_DATES_VALUE;

  const handleSelect = (value: string) => {
    onDateChange(value);
    setOpen(false);
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
          <div className="w-full sm:w-auto sm:min-w-[320px]">
            <Label>Filtrar por data</Label>
            <div className="flex gap-2 mt-1">
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger
                  role="combobox"
                  aria-expanded={open}
                  className="flex h-10 flex-1 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  type="button"
                >
                  <div className="flex items-center gap-2">
                    <CalendarDays className="size-4 text-gray-500 shrink-0" />
                    <span className="truncate">
                      {selectedDate === ALL_DATES_VALUE
                        ? 'Todos os dias'
                        : selectedDate
                        ? formatCompactDate(selectedDate)
                        : 'Selecione um dia'}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] sm:w-[320px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Pesquisar data..." />
                    <CommandList>
                      <CommandEmpty>Nenhuma data encontrada.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="todos-os-dias-all"
                          onSelect={() => handleSelect(ALL_DATES_VALUE)}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedDate === ALL_DATES_VALUE ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <span className="font-medium">Todos os dias</span>
                        </CommandItem>
                      </CommandGroup>

                      {availableDates.length > 0 && (
                        <>
                          <CommandSeparator />
                          <CommandGroup heading="Datas disponíveis">
                            {availableDates.map((date) => (
                              <CommandItem
                                key={date}
                                value={formatSearchableDate(date)}
                                onSelect={() => handleSelect(date)}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedDate === date ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div className="flex flex-col">
                                  <span className="font-medium">{formatShortDate(date)}</span>
                                  <span className="text-xs text-gray-500 capitalize">
                                    {new Date(date + 'T00:00:00').toLocaleDateString('pt-BR', { weekday: 'long' })}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {!isAllSelected && selectedDate && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => onDateChange(ALL_DATES_VALUE)}
                  title="Limpar filtro"
                  type="button"
                >
                  <X className="size-4" />
                </Button>
              )}
            </div>
          </div>
          {selectedDate && (
            <div className="text-sm text-gray-600 mb-3">
              {isAllSelected
                ? 'Mostrando dados de todos os dias'
                : `Mostrando dados de ${formatDateLabel(selectedDate)}`
              }
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
